import React, { ReactNode, useCallback, useState } from 'react';
import { EMPTY_RICH_TEXT_CONTENT } from '../../../components/RichTextEditor';
import { DropType, IGridDropPosition } from '../../../components/DndGrid/types';
import DashboardAddContentMenuItems from '../DashboardInstance/components/DashboardAddContent/DashboardAddContentMenuItems';
import { DashboardMediaPopoverMode } from '../DashboardInstance/components/DashboardMediaPopover/types';
import { MaybeNil } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import { IDashboardInstanceFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import useDashboardAddReport from './add/useDashboardAddReport';
import useDashboardAddImageEmbed from './add/useDashboardAddImageEmbed';
import useDashboardAddExternalEmbed from './add/useDashboardAddExternalEmbed';
import useDashboardAddRichText from './add/useDashboardAddRichText';
import useDashboardMediaPopoverController from '../DashboardInstance/components/DashboardMediaPopover/useDashboardMediaPopoverController';
import { DASHBOARD_MAX_COLUMNS_PER_ROW } from './constants';

/**
 * Combines all add operations on the dashboard. Uses individual useDashboardAdd* hooks to perform the add operation
 *
 * @param dashboard - the current dashboard
 * @param showCreateNewOption - if "Create New" is available or not
 */
const useDashboardAddContent = (
  dashboard: IDashboardInstanceFieldsFragment,
  showCreateNewOption: boolean,
) => {
  const [loading, setLoading] = useState(false);
  const { startImageEmbedAddUpload, imageEmbedUploadProps } =
    useDashboardAddImageEmbed(dashboard, setLoading);
  const { addDashboardExternalEmbed } = useDashboardAddExternalEmbed(dashboard);
  const { addRichText } = useDashboardAddRichText(dashboard);
  const { addReport } = useDashboardAddReport(dashboard);
  const { popoverProps, openPopover } = useDashboardMediaPopoverController();

  const { baseId, rows } = dashboard;

  const createMenuItems = useCallback<
    (position?: MaybeNil<IGridDropPosition>) => ReactNode
  >(
    (position) => {
      const canAdd =
        isNil(position) ||
        position.type === DropType.ROW || // always allow adding a new row...
        //  but check if current row does not exceed the limit
        rows[position.rowIndex].columns.length < DASHBOARD_MAX_COLUMNS_PER_ROW;

      return (
        <DashboardAddContentMenuItems
          baseId={baseId}
          onAddImage={() => startImageEmbedAddUpload(position)}
          onAddMedia={(e) =>
            openPopover({
              url: null,
              anchorPosition: { left: e.clientX, top: e.clientY },
              mode: DashboardMediaPopoverMode.Add,
              onSubmit: async (url) => {
                setLoading(true);
                try {
                  await addDashboardExternalEmbed({ url, position });
                } finally {
                  setLoading(false);
                }
              },
            })
          }
          onAddReport={async () => {
            setLoading(true);
            try {
              await addReport(position);
            } finally {
              setLoading(false);
            }
          }}
          onAddRichText={async () => {
            setLoading(false);
            try {
              await addRichText({ html: EMPTY_RICH_TEXT_CONTENT, position });
            } finally {
              setLoading(false);
            }
          }}
          disableOptions={!canAdd}
          showCreateNewOption={showCreateNewOption}
        />
      );
    },
    [
      rows,
      baseId,
      showCreateNewOption,
      startImageEmbedAddUpload,
      openPopover,
      addDashboardExternalEmbed,
      addReport,
      addRichText,
    ],
  );

  return {
    loading,
    createMenuItems,
    popoverProps,
    imageEmbedUploadProps,
  };
};

export default useDashboardAddContent;
